import { BusinessPlanType, Payments, Roles } from '@prisma/client';
import posthog from 'posthog-js';
import API_URL from '../apiconfig';
import { Business, Employee, LoadedUser, User } from '../interfaces';
import { CreateCustomerValues } from '../utils/form/customerForm';
import axios from './api';
import client from '../utils/react-query/client';
import { CustomerOverview } from '../types/customers';

export async function getUser() {
  try {
    const user = await axios.get(`${API_URL}/v1/users/me`);

    return user.data.result;
  } catch (error) {
    throw new Error('Cannot get user');
  }
}

export async function getCustomTokenForUser(accessToken: string) {
  try {
    const customToken = await axios.get(`${API_URL}/v1/users/custom`, {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    });

    return customToken.data.result as string | null;
  } catch (error) {
    throw new Error('Cannot get custom token');
  }
}

export async function searchCustomers(customerPhone: string) {
  const searchString = `+${customerPhone}`;

  try {
    const customer = await axios.get(
      `${API_URL}/v1/search/customers?limit=1&phone=${encodeURIComponent(
        searchString,
      )}`,
    );

    return customer.data.result;
  } catch (error) {
    throw new Error('Cannot find customer');
  }
}

export async function getCustomerById(customerId: string) {
  try {
    const customer = await axios.get(`${API_URL}/v1/customers/${customerId}`);

    return customer.data.result;
  } catch (error) {
    throw new Error('Cannot find customer');
  }
}

export async function getCustomerOverview(
  customerId: string,
  businessId?: string,
) {
  try {
    const customer = await axios.get(
      `${API_URL}/v1/customers/overview/${customerId}`,
      {
        params: {
          businessId,
        },
      },
    );

    return customer.data.result as CustomerOverview;
  } catch (error) {
    throw new Error('Cannot find customer');
  }
}

export function createFullUser(
  user: User,
  businesses: Business[],
  employee: Employee,
  selectedBusiness?: string,
) {
  const queryParamBusinessId = new URLSearchParams(window.location.search).get(
    'business',
  );

  const paramBusiness = queryParamBusinessId
    ? businesses.find(b => b.businessId === queryParamBusinessId)
    : undefined;

  const business =
    paramBusiness ??
    businesses.find(b => b.businessId === selectedBusiness) ??
    businesses[0];
  const employeeToBusiness =
    employee.employeesToBusinesses?.find(
      e => e.businessId === selectedBusiness,
    ) ?? employee.employeesToBusinesses?.[0];

  // Save selected business to session storage - could be possible that previous business is not found in the list of businesses
  sessionStorage.setItem('selectedBusiness', business.businessId);

  const entityType =
    business.type === BusinessPlanType.STUDIO ? 'business' : 'employee';
  const entityId =
    business.type === BusinessPlanType.STUDIO
      ? business.businessId
      : employee.employeeId;

  posthog.identify(user.userId);
  posthog.group(entityType, entityId);

  const canViewReports = !!employeeToBusiness?.canViewReports;
  const canManageTeam = !!employeeToBusiness?.canManageTeam;
  const canManageWaivers = !!employeeToBusiness?.canManageWaivers;
  const canIssueRefunds = !!employeeToBusiness?.canIssueRefunds;
  const canManageCampaigns = !!employeeToBusiness?.canManageCampaigns;
  const canViewAllShopCustomers = !!employeeToBusiness?.canViewAllShopCustomers;
  const generalRequestOwner = !!employeeToBusiness?.generalRequestOwner;

  const fullUser: LoadedUser = {
    ...user,
    canViewReports,
    canManageTeam,
    canManageWaivers,
    canIssueRefunds,
    canManageCampaigns,
    canViewAllShopCustomers,
    generalRequestOwner,
    business: {
      id: business.businessId,
      name: business.name,
      urlPath: business.urlPath,
      coverImage: business.coverImage,
      thumbnailImage: business.thumbnailImage,
      city: business.city,
      state: business.state,
      zip: business.zip,
      streetAddress: business.streetAddress,
      streetAddress2: business.streetAddress2,
      employees: [],
      services: business.services,
      defaultTattooDeposit: business.defaultTattooDeposit,
      terminalReaderId: business.terminalReaderId,
      businessTaxRates: business.businessTaxRates,
      inStoreCustomerServiceFeeRate: business.inStoreCustomerServiceFeeRate,
      onlineCustomerServiceFeeRate: business.onlineCustomerServiceFeeRate,
      type: business.type,
      tattooAftercareLink: business.tattooAftercareLink,
      reviewLink: business.reviewLink,
      allowGeneralRequests: business.allowGeneralRequests,
      stationMax: business.stationMax,
      businessTrips: business.businessTrips,
      disableCreditTipping: business.disableCreditTipping,
      inactiveEmployeesToBusinesses: business.inactiveEmployeesToBusinesses,
      isTerminalPurchased: business.isTerminalPurchased,
    },
    allBusinesses: businesses,
  };

  if (business.employeesToBusinesses) {
    // Sort employees by sortOrder if it exists, otherwise add to end
    const sortedEmployees = [...business.employeesToBusinesses].sort((a, b) => {
      // If both have sortOrder, compare them
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      // If only a has sortOrder, it comes first
      if (a.sortOrder !== undefined) {
        return -1;
      }
      // If only b has sortOrder, it comes first
      if (b.sortOrder !== undefined) {
        return 1;
      }
      // If neither has sortOrder, maintain original order
      return 0;
    });

    sortedEmployees.forEach(employeeMapping => {
      fullUser.business.employees.push({
        payrollType: employeeMapping.payrollType,
        depositToEmployeePercent: employeeMapping.depositToEmployeePercent,
        tipToEmployeePercent: employeeMapping.tipToEmployeePercent,
        paymentToEmployeePercent: employeeMapping.paymentToEmployeePercent,
        isBookable: employeeMapping.isBookable,
        bonusCommissionPercent: employeeMapping.bonusCommissionPercent,
        addToAllChats: employeeMapping.addToAllChats,
        canViewReports: employeeMapping.canViewReports,
        canManageWaivers: employeeMapping.canManageWaivers,
        canIssueRefunds: employeeMapping.canIssueRefunds,
        canManageTeam: employeeMapping.canManageTeam,
        canManageCampaigns: employeeMapping.canManageCampaigns,
        canViewAllShopCustomers: employeeMapping.canViewAllShopCustomers,
        generalRequestOwner: employeeMapping.generalRequestOwner,
        payeeForOtherPayroll: employeeMapping.payeeForOtherPayroll,
        selectedRoles: employeeMapping.selectedRoles,
        color: employeeMapping.color,
        sortOrder: employeeMapping.sortOrder,
        ...employeeMapping.employee,
      });
    });
  }

  return fullUser;
}

export async function addCustomerDatabase(
  values: CreateCustomerValues,
  businessId?: string,
) {
  // Filter out fields with empty string values
  const filteredValues = Object.entries(values).reduce(
    (acc: Partial<CreateCustomerValues>, [key, value]) => {
      if (value !== '') {
        acc[key as keyof CreateCustomerValues] = value;
      }
      return acc;
    },
    {} as Partial<CreateCustomerValues>,
  );

  const customer = await axios.post(`${API_URL}/v1/customers`, {
    ...filteredValues,
    phoneNumber: `+${values.phoneNumber}`,
    businessId,
  });

  return customer.data.result;
}

export async function getCustomerTransactions(
  customerId: string | undefined,
  businessId: string,
) {
  try {
    const transactions = await axios.get(
      `${API_URL}/v1/customers/transactions/${customerId}`,
      { params: { businessId } },
    );
    return transactions.data.result as Payments[];
  } catch (error: unknown) {
    // Check if the error is an Axios error with a response and a 404 status
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('Unknown error occurred');
    }
  }
}

export async function addCustomerAlgolia(
  customerId: string,
  businessId: string,
) {
  try {
    const algoliaResponse = await axios.post(
      `${API_URL}/v1/customers/algolia`,
      { customerId, businessId },
    );

    return algoliaResponse.data.result;
  } catch (error) {
    throw new Error('Cannot add customer to search index');
  }
}

export async function updateCustomer(
  values: CreateCustomerValues,
  customerId: string,
  businessId?: string,
) {
  // Filter out fields with empty string values
  const filteredValues = Object.entries(values).reduce(
    (acc: Partial<CreateCustomerValues>, [key, value]) => {
      const allowEmptyString = ['lastName', 'internalNotes'];
      if (value !== '' || allowEmptyString.includes(key)) {
        acc[key as keyof CreateCustomerValues] = value;
      }
      return acc;
    },
    {} as Partial<CreateCustomerValues>,
  );

  const phoneNumber =
    filteredValues.phoneNumber?.charAt(0) === '+'
      ? filteredValues.phoneNumber
      : `+${filteredValues.phoneNumber}`;

  const customer = await axios.patch(`${API_URL}/v1/customers/${customerId}`, {
    ...filteredValues,
    phoneNumber,
    businessId,
  });

  return customer.data.result;
}

export async function getRoleByPhoneNumber(phoneNumber: string) {
  try {
    const role = await axios.get(`${API_URL}/v1/users/role/${phoneNumber}`);

    return role.data.result as Roles | undefined;
  } catch {
    return undefined;
  }
}

export async function refetchCustomerPayments(customerId: string) {
  const key = ['customer-payments', customerId];
  await client.refetchQueries({ queryKey: key, exact: false });
}

export async function invalidateCustomerPayments(customerId: string) {
  const key = ['customer-payments', customerId];
  await client.invalidateQueries({ queryKey: key, exact: false });
}
